/** @file previewClip: the drawn clip, a pose override and isolate hiding over the stored one. */
import type { Clip, ClipKey, NodeKey } from '@zilo/pose3d-format/types/clip';
import type { PoseOverride } from '../../state/editorTypes';

/** `nodes` merged over the key's nodes (a node named in both takes the override's key whole). */
const withNodes = (key: ClipKey, nodes: Record<string, NodeKey>): ClipKey => ({ ...key, nodes: { ...key.nodes, ...nodes } });

/** The keys with `override.nodes` written at its frame (a key is added there when the clip has none). */
function overridden(keys: readonly ClipKey[], override: PoseOverride | null): ClipKey[] {
  if (override === null) return [...keys];
  const at = keys.find((k) => k.frame === override.frame);
  const rest = keys.filter((k) => k.frame !== override.frame);
  return [...rest, withNodes(at ?? { frame: override.frame, nodes: {} }, override.nodes)].sort((a, b) => a.frame - b.frame);
}

/** Every key, and a frame-0 key, marks the `hidden` nodes hidden (so no key of theirs shows them again). */
function hiding(keys: ClipKey[], hidden: readonly string[]): ClipKey[] {
  if (hidden.length === 0) return keys;
  const all = keys.some((k) => k.frame === 0) ? keys : [{ frame: 0, nodes: {} }, ...keys];
  return all.map((k) => withNodes(k, Object.fromEntries(hidden.map((n) => [n, { ...k.nodes[n], hidden: true }]))));
}

/**
 * The stored clip itself when there is nothing to show (no override for this clip, nothing hidden); otherwise a new
 * clip object (pose memoises channels per clip object, so a new object is what makes the runtime re-read the keys).
 */
export function previewClip(clip: Clip, override: PoseOverride | null, hidden: readonly string[]): Clip {
  const own = override !== null && override.clip === clip.name ? override : null;
  if (own === null && hidden.length === 0) return clip;
  return { ...clip, keys: hiding(overridden(clip.keys, own), hidden) };
}
